<template>
  <div class="bg-white rounded-sm mx-2 my-3">
    <div class="grid grid-cols-12 gap-4">
      <div class="col-span-12 mx-3 mt-3">
        <div class="grid grid-cols-12 gap-4">
          <div class="col-span-12">
            <span class="title">Insolvency Practitioner</span>
          </div>
          <div class="col-span-2 mx-auto text-center">
            <button class="btn-primary bg-primary rounded px-2 py-1 text-white hover:bg-primary-dark" @click="back">
              <i class="fa fa-arrow-left text-white mr-2"></i>Back
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="grid grid-cols-12 gap-4 mx-3 mt-2">
      <div class="col-span-12 mx-auto bg-gray-10 py-3 rounded">
        <span class="text-gray">Practitioner Number / {{ practitionerNumber }}</span>
      </div>
    </div>

    <div class="grid grid-cols-12 gap-4 mx-3 py-3">
      <div class="col-span-12 relative">
        <!-- Loading Overlay -->
        <div v-if="loading" class="absolute inset-0 bg-gray-50 bg-opacity-75 flex items-center justify-center rounded z-50">
          <div class="text-center">
            <i class="fa fa-spinner fa-spin fa-3x text-primary"></i>
            <p class="mt-2 text-sm text-gray-600">Loading...</p>
          </div>
        </div>

        <div class="col-span-12">
          <div class="grid grid-cols-12 gap-4">
            <div class="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3 xl:col-span-2">
              <img v-if="avatarUrl" :src="avatarUrl" class="rounded-full w-100 border-2 border-primary" alt="avatar"/>
            </div>
          </div>
          <div class="grid grid-cols-12 gap-4">
            <div class="col-span-12 mt-3">
              <span class="font-bold text-primary h4">{{ practitionerNumber }}</span><br/>
              <span class="font-bold text-gray h4 my-auto mr-2 text-capitalize">{{ practitioner.firstName }} {{ practitioner.lastName }}</span>
              <br/>
              <span v-if="profession !== null" class="text-gray text-sm text-capitalize">Profession: {{ profession.professionName }}</span>
              <br/>
              <br/>
              <span class="text-gray text-sm text-capitalize">{{ practitioner.phoneNumber }}</span><br/>
              <span class="text-gray text-sm text-lowercase">{{ practitioner.email }}</span><br/>
              <span class="text-primary text-xs">Registration Date: {{
                  dateFormat(practitioner.registrationDate || "")
                }}</span>
              <br/>
              <span class="text-gray text-sm">Practitioner Account Status:
                <span v-if="practitioner.active" class="px-2 py-1 bg-green rounded-sm text-white text-xs my-auto">ACTIVE</span>
                <span v-else class="px-2 py-1 bg-red rounded-sm text-white text-xs my-auto">INACTIVE</span>
              </span>
              <br/><br/>
              <div class="grid grid-cols-12 gap-4">
                <div class="col-span-12">
                  <PractitionerDetails
                    :data="practitioner.form1s[0]"
                    :upn="practitionerNumber"
                    :form1s="practitioner.form1s"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { usePractitionersRegistryStore } from "@/stores/practitioners-registry";
import { dateFormat } from "@/commons/composables/utilities";
import PractitionerDetails from "./PractitionerDetails.vue";
import { PassportPhoto, Practitioner, Profession } from "@/domains/home/services/registry/practitioners/types";

const route = useRoute()
const store = usePractitionersRegistryStore();

const practitionerNumber = ref<string>("");
const loading = ref(false)
const practitioner = ref<Practitioner>({
  active: true,
  form1s: []
})

onMounted(() => {
  practitionerNumber.value = route.params.id as string
  fetch()
})

const fetch = () => {
  loading.value = true
  store.fetchPractitionerDetails(practitionerNumber.value);
}

const back = () => {
  window.history.back()
}

const profession = computed(() => {
  if (practitioner.value.form1s.length === 0) {
    return null
  }

  let professions: Profession[] = []

  practitioner.value.form1s.forEach(form1 => {
    form1.professions.forEach(profession => {
      professions.push(profession)
    })
  })

  if (professions.length === 0) {
    return null
  }

  return professions[0]
})

const avatar = computed(() => {
  if (practitioner.value.form1s.length === 0) {
    return null
  }

  let passportPhotos: PassportPhoto[] = []

  practitioner.value.form1s.forEach(form1 => {
    form1.passportPhotos.forEach(photo => {
      passportPhotos.push(photo)
    })
  })

  if (passportPhotos.length === 0) {
    return null
  }

  return passportPhotos[0]
})

const avatarUrl = computed(() => {
  if (avatar.value === null) {
    return undefined
  } else {
    return import.meta.env.VITE_API_BASE_URL + '/insolvency/api/practitioner-registration/passport-photo/' + avatar.value.filename
  }
})

watch(() => store.practitioner_details, (data) => {
  if (data?.data) {
    loading.value = false
    practitioner.value = data.data
  }
})
</script>
